import type { IconProps } from "nucleo-ui-outline-18";
import type { FC } from "react";

/**
 * ICON_SIZE is the standard icon-size scale used across all components. Icons come
 * from the Nucleo UI family, whose 18px grid is `md` — the size to reach for unless
 * a surface has a reason to differ.
 */
export const ICON_SIZE = {
  lg: 24,
  md: 18,
  sm: 16,
  xl: 28,
  xs: 14,
} as const;

export type IconSize = (typeof ICON_SIZE)[keyof typeof ICON_SIZE];

/**
 * Icon is the component type every Nucleo glyph satisfies, whatever family, size or
 * variant it comes from — they all take `size`, `title` and the standard SVG props.
 * Declared once here so an icon registry (a name-to-glyph map, a menu action, a
 * category style) types its values against one alias rather than reaching into a
 * specific icon package for a type that is identical across all of them.
 *
 * Rendering a glyph filled means importing it from a `*-fill-*` package instead of
 * an `*-outline-*` one — the two are separate components, so there is no class or
 * prop that toggles fill.
 */
export type Icon = FC<IconProps>;

/**
 * SWATCH_SIZE is the diameter in pixels of the round color swatch/disk that anchors
 * a studio editor row: the segment color circle, the waypoint icon disk, and the
 * suggestion candidate swatch. It is sized to span a row's two text lines so the
 * swatch reads as the row's color spine. Shared here so those surfaces stay the same
 * size and cannot drift apart.
 */
export const SWATCH_SIZE = 28;

/**
 * SWATCH_SECTION_WIDTH is how wide an input's leading section must be to hold a
 * {@link SWATCH_SIZE} swatch with the gap that keeps the typed value off it. Mantine
 * sizes a left section from one number, so the swatch's size and that gap have to be
 * added together somewhere — and doing it at the call site is how the sum drifts the
 * moment the swatch is resized. Stated here, beside the term it is built from.
 */
export const SWATCH_SECTION_WIDTH = SWATCH_SIZE + 12;
